/**
 * 我的 · TA 的形象（/me/look）：从“我的”进来的全屏页，左上角回 /me，不挂底栏（方案第 9 节“TA 的形象（调整形象）”），
 * 写法照 /me/dna、/me/reports（WorldGate 守）。
 * - 复用小窝的 usePetCharacter（纯读形象）与 AdjustCharacter（“调整形象”，里面已带证件照一节 IdPhotoAdjust），
 *   只读引用 home/PetFigure.tsx，不改它。按钮出不出现全由它们按能力表与服务端的 can_regenerate、证件照状态决定，这里不另加按钮。
 * - 顶部是 TA 现在的样子：头像一律 PetPortrait（有照片用照片，演示用演示小灰猫，live 没照片是爪印，不写名字首字）；
 *   已有生效的星球形象时，下面再放那张站姿图（只放 sceneAsset 认可、验过透明的那张，按中性站姿的身体高度落位）。
 *   头像旁那一句按 lookHeadline 分，和下面形象、证件照两节用同一组事实，读起来不打架（2026-09-25 逐个状态理过）。
 * - 一句说明：形象和证件照都照着 TA 的照片画；这一页真有“画证件照”按钮时，才顺带说老伙伴在这里补第一张证件照
 *   （能力表里证件照没开、服务端这会儿不让发起时，这句不提按钮）；没有照片的伙伴（no_reference_photo）这句只说照片是来源
 *   （画不了由下面两节各说一次），不许诺页面上没有的按钮。
 * - 补一张照片（PUT /pets/{id}/photo，不带 replace_version）：只在这只宠物一张照片都没有、而且是自己的宠物时出现，见 canAddPhoto；
 *   成功后刷新相关缓存（含这只的形象），页面换成照片。
 * - 换一张照片（同一个 PUT，主人确认后带 replace_version，2026-09-25 用户拍板）：自己的宠物已有照片（上传的，或没照片时
 *   生成的形象照）时出现，见 canReplacePhoto；领养来的伙伴不给。
 * - 补好、换好之后说的那一句（PhotoNote）一页只留最新的一句：主人开始下一次换照片（选好一张图）时，上一句就收掉。
 * - 补照片、换照片两张卡里，“照片只有家里人看得到”后面补一句照片交给外部图像服务处理（DATA-USE-COPY-01），带“查看内容使用说明”，
 *   原地打开说明（不离开这一页，选好的图不丢）；不写“上传”（这一页整页不出现“上传”）、不写“画”。
 * - 照片一变（补上、换掉），后端自动排一次证件照（用户 2026-09-26 拍板）；星球形象不跟着画，仍由主人点“调整形象”。
 *   这一页只读：刷新形象查询，证件照在排、在画时每 10 秒复查一次（只发 GET），证件照那块先放现在的照片、说“证件照正在画”，
 *   不转圈；绝不替主人发生成请求（下面证件照一节的按钮照现有规则出现，主人点了才发）。
 * - 读的状态分清：能力表说形象没开 → 说暂时看不了，不发形象请求；还在读 → 等一等；读失败 → 统一错误态可重试，
 *   不当成“没有形象”。演示模式没有真实形象：温和说明，不编数据、不放按钮。
 */
import { BodyInfoCard } from "./BodyInfo";
import { WorldBodyCare } from "./WorldBodyCare";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CharacterAsset, CharacterState, PetOrigin } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { Button, ErrorState, Icon, LoadingState, Page, TopBar } from "@/shared/ui";
import { AdjustCharacter, characterNote, characterReasonCode, isGeneratedReferenceOnly, referenceBodyHeight, sceneAsset, usePetCharacter } from "@/features/home/PetFigure";
import { PetPortrait } from "@/features/pets/PetPortrait";
import { PhotoDataUseNote } from "@/features/identity/dataUse/DataUseNote";
import { isPhotoUnclear, photoRejectedForSure, photoUnclearText, photoBeforeSend, photoNotReady, photoResultUncertain } from "@/features/pets/photoRetry";
import { PhotoUnknownSelection, type PhotoSubmission } from "@/features/pets/photoUnknownSelection";
import { WorldGate } from "@/features/world_map/WorldGate";
import { useCurrentPet, type CurrentPet } from "@/features/memories/currentPet";
import "./me.css";
import "./look.css";

/** 页面上那一句说明：这一页真的会出现“画证件照”按钮时，才顺带告诉老伙伴在哪儿补第一张证件照。 */
export const LOOK_INTRO_WITH_ID_PHOTO = "TA 在星球上的样子和证件照，都照着 TA 的照片来画；早些住进来、还没有证件照的伙伴，在这里点“画证件照”补一张最方便。";
export const LOOK_INTRO = "TA 在星球上的样子照着 TA 的照片来画；画好以后觉得不像，可以在这里调整。";
/**
 * 没有照片（服务端 blocked_reason = no_reference_photo）：这句只说照片是形象和证件照的来源；“现在画不了”由下面形象、证件照
 * 两节各自那句（“还没有 TA 的照片，暂时没法准备形象 / 证件照。”）说，这里不再说第三遍。
 * 不提“画证件照”；补照片的入口是上面单独的一张卡（AddPhoto），这句不重复说它（2026-09-24 巡检 P1 → 接口 add_pet_photo 落地后加入口）。
 */
export const LOOK_INTRO_NO_PHOTO = "TA 在星球上的样子和证件照，都要照着 TA 的照片来画。";
/**
 * 能力表说这个环境画不出形象（character.regenerate 为 not_configured，W5，c84a D2116-4）：不说“可以在这里调整”；
 * “现在还画不了”由下面形象一节那句（CHARACTER_NOT_DRAWABLE_NOTE）说，这里只说形象从哪儿来。
 */
export const LOOK_INTRO_NOT_DRAWABLE = "TA 在星球上的样子照着 TA 的照片来画。";

export const ADD_PHOTO_PRIVACY = "照片只有家里人看得到。";
export const ADD_PHOTO_DONE = "照片放好了，只有家里人看得到。";
/** 图用不了（预览打不开，或服务端 MEDIA_REJECTED）：同一句。 */
export const ADD_PHOTO_UNREADABLE = "这张图用不了，换一张试试。";
/** 识别没配好（not_ready，见 photoRetry）：再试也一样，不给再试。后端那句说的是「带 TA 进来」，这一页 TA 已经在家，说页内的话（UI-PHOTO-RECOVERY-RETRYABLE-01） */
export const ADD_PHOTO_NOT_READY = "现在还放不了照片，先不补也没关系。";
export const REPLACE_PHOTO_NOT_READY = "现在还放不了照片，先用现在这张。";

export const REPLACE_PHOTO_ASK = "要换掉现在这张吗？";
/**
 * 问“要换掉吗”时顺带说一句换了以后会怎样（2026-09-26 起照片一变后端自动重画证件照；星球形象不跟着画，这里不提）。
 * 画好前证件照先用着旧的，下面证件照一节会如实说“还是照着旧照片的”。
 */
export const REPLACE_PHOTO_ID_NOTE = "换好后，证件照会照新照片重画。";
export const REPLACE_PHOTO_DONE = "照片换好了。";
/** 主人确认之后，照片又被家里别人换过（409 photo_changed）：页面已刷新成家里人放的那张，等主人再决定，不自动重发。 */
export const REPLACE_PHOTO_CHANGED = "家里人刚刚换过 TA 的照片。先看看现在这张，再决定要不要换。";

/**
 * 补一张照片的入口出不出现。依据是 /households 里这只宠物的事实（和 PetPrivateSummary 同一处来的 photo_url、origin），不猜：
 * - photo_url 为空才出现。没照片时服务端生成的那张形象照也放在同一个照片位上（PetPrivateSummary.photo_generated = true，
 *   photo_url 不为空），所以它在就不算“没有照片”——换掉它走“换一张照片”（canReplacePhoto）；
 * - origin 必须是 own_pet：领养来的伙伴用的是 TA 在星球上的样子；
 * - 只在 live：演示模式不上传照片。
 */
export function canAddPhoto(photoUrl: string | null, origin: PetOrigin | null): boolean {
  return env.dataMode === "live" && origin === "own_pet" && !photoUrl;
}

/**
 * 换一张照片的入口出不出现：live、自己的宠物、已经有照片——上传的，或没照片时生成的形象照（用户 2026-09-25 拍板：生成的也能换）。
 * 领养来的伙伴不给（服务端也会回 not_own_pet）；演示模式不上传照片。
 */
export function canReplacePhoto(photoUrl: string | null, origin: PetOrigin | null): boolean {
  return env.dataMode === "live" && origin === "own_pet" && Boolean(photoUrl);
}

/** 补照片失败说人话：服务端的三种原因各配一句，其余用统一的玩家说法（不露错误码）。 */
export function addPhotoFailureText(error: unknown, unclearTimes = 1): string {
  const err = toApiError(error);
  const reason = typeof err.details?.reason === "string" ? err.details.reason : null;
  if (reason === "photo_exists") return "TA 已经有照片了。";
  if (reason === "not_own_pet") return "领养来的伙伴用的是 TA 在星球上的样子。";
  // 10-03 起（2531 rc1900 卡住，I、P、c84a 1046Z 定）：这会儿没认出来——同一张照片可以“重新识别”（换新键）；没确认好——稍后再试（同键重放）。见 photoRetry
  if (photoResultUncertain(err)) return "这张照片的结果还没确认。可以选另一张照片，原来的操作先保留。";
  if (isPhotoUnclear(err)) return photoUnclearText(unclearTimes);
  if (photoNotReady(err)) return ADD_PHOTO_NOT_READY;
  if (photoBeforeSend(err)) return "这张照片这次还没送出，可以主动再试一次。";
  if (err.code === "MEDIA_REJECTED") return ADD_PHOTO_UNREADABLE;
  return err.playerMessage;
}

/**
 * 换照片时显示的那一句。photo_exists 不是出错：是服务端要主人先确认（换照片的正常一步），不显示；
 * photo_changed 说家里人刚换过；其余同补照片。
 */
export function replacePhotoFailureText(error: unknown, unclearTimes = 1): string | null {
  const apiError = toApiError(error);
  if (photoResultUncertain(apiError)) return addPhotoFailureText(apiError, unclearTimes);
  if (photoNotReady(apiError)) return REPLACE_PHOTO_NOT_READY;
  const reason = apiError.details?.reason;
  if (reason === "photo_exists") return null;
  if (reason === "photo_changed") return REPLACE_PHOTO_CHANGED;
  return addPhotoFailureText(error, unclearTimes);
}

/**
 * 头像旁那一句。每种状态下它说的和下面两节说的是同一回事（2026-09-25 主窗口：原来领养来的伙伴顶部写“还没有星球形象”，
 * 下面又说“TA 在星球上的样子就是这张”，读着拧）：
 * - 有能放进场景的形象（含已经过时的 outdated、新的一版在画或没画成——场景里仍是这一套）→ 就是下面这个样子；
 * - 只有生成的形象照（原因码与下面形象一节取法相同：characterReasonCode，在排、在画、结果没确认时不算）：
 *   领养来的伙伴 → 就是这个样子（下面说“TA 在星球上的样子就是这张”）；自己的宠物 / 不知道来历 → 暂时是按描述画的
 *   （下面说“现在只有 / 现在用的是按描述画出来的样子”）；
 * - 其余照小窝同一套短说法（characterNote：正在准备 / 这次没画成 / 结果还没确认 / 被挡住时“还没有星球形象”，
 *   原因由下面说）；都不是时：有照片 → 星球上暂时用 TA 的照片，没有 → 还没有星球形象。
 * 每句都在“TA”两边留着空格：窄屏只在这里断行（look.css 的 keep-all），不把“样子”“描述”拆开。
 */
export const LOOK_HEADLINE = {
  drawn: "星球上的 TA 就是下面这个样子",
  portrait: "星球上的 TA 就是这个样子",
  described: "星球上的 TA 暂时是按描述画的",
  photo: "星球上暂时用 TA 的照片",
  none: "还没有星球形象",
} as const;

export function lookHeadline(state: CharacterState, { photoUrl, origin }: { photoUrl: string | null; origin: PetOrigin | null }): string {
  if (sceneAsset(state)) return LOOK_HEADLINE.drawn;
  if (onlyGeneratedReference(state)) return origin && origin !== "own_pet" ? LOOK_HEADLINE.portrait : LOOK_HEADLINE.described;
  return characterNote(state) ?? (photoUrl ? LOOK_HEADLINE.photo : LOOK_HEADLINE.none);
}

/**
 * 形象一节此刻是不是按“只有生成的形象照”在说（和 AdjustCharacter 同一个取法 characterReasonCode：candidate.reason ?? blocked_reason，
 * 在排、在画、结果没确认时不算——没确认时下面一律说“上一次形象的结果还没确认。”，2026-09-26）。
 * 顶部那一句、换照片那张卡的标题都跟着它，和下面那句“在上面补一张 TA 的真实照片”说的是同一回事。
 */
function onlyGeneratedReference(state: CharacterState): boolean {
  return isGeneratedReferenceOnly(characterReasonCode(state));
}

/**
 * 换照片那张卡的标题。现在这张是没照片时生成的形象照时，和下面形象一节同一个说法“补一张 TA 的真实照片”（巡检 R7-G0：
 * 原来这里写“换一张”、下面写“补一张”）；其余是“换一张 TA 的照片”。
 */
export const REPLACE_PHOTO_TITLE = "换一张 TA 的照片";
export const REPLACE_PHOTO_TITLE_REAL = "补一张 TA 的真实照片";
/**
 * 站姿图旁边那一句：形象是照着之前那张照片画的（outdated，巡检 R6-18 挪到图旁）。形象不自动重画，照旧这么说。
 * 2026-09-26 起证件照换照片后会自动重画，证件照旁边那句另说（ID_PHOTO_OUTDATED_NOTE“证件照还是照着旧照片的。”），两句不再共用。
 */
export const FIGURE_OUTDATED_NOTE = "这是照着之前那张照片画的。";

/**
 * 照片那两张卡说的那一句（补好了、换好了、入口其实不该出现）：一页只留最新的一句（2026-09-26 巡检 R12-S2：同一次打开页面里
 * 先补照片、再换照片，卡外“照片放好了……”和卡里“照片换好了。”同时挂着）。换照片那张卡选好一张图（下一次操作开始）时收掉上一句。
 * - page：说在两张卡外面（补照片那张卡补好以后就不在了；入口其实不该出现时也在这里说）；
 * - replace：说在换照片那张卡里（“照片换好了。”）。
 */
type PhotoNote = { text: string; where: "page" | "replace" };

/** 这一页会不会出现“画证件照”：和证件照那一节（home/PetFigure.tsx 的 IdPhotoAdjust）同一组条件——能力开着、服务端允许、还没有证件照或上次没画成。 */
function offersIdPhotoButton(state: CharacterState, idPhotoOn: boolean): boolean {
  const status = state.id_photo?.status ?? "absent";
  return idPhotoOn && state.can_regenerate === true && (status === "absent" || status === "failed");
}

type Look =
  | { phase: "demo" }
  | { phase: "checking" }
  | { phase: "off" }
  | { phase: "loading" }
  | { phase: "error"; error: unknown; retry: () => void }
  | { phase: "ready"; state: CharacterState; idPhotoOn: boolean; notDrawable: boolean };

export function LookPage() {
  return (
    <WorldGate>
      <LookBody />
    </WorldGate>
  );
}

function LookBody() {
  const current = useCurrentPet();
  return (
    <Page bare className="ps-look">
      {/* 顶栏用全站那一种（UI-BASE-01b，总账 V-03） */}
      <TopBar title="TA 的形象" back="/me" backLabel="返回我的" />
      {current.status === "pending" ? (
        <LoadingState lines={2} label="正在找到 TA…" />
      ) : current.status === "error" ? (
        <ErrorState error={current.error} onRetry={current.retry} />
      ) : current.pet ? (
        <LookForPet key={current.pet.petId} pet={current.pet} />
      ) : (
        <p className="ps-look-quiet" role="status">
          还没有住进来的伙伴。
        </p>
      )}
    </Page>
  );
}

/**
 * 形象读到哪一步了。数据只从 usePetCharacter 拿（它按能力表决定发不发请求）；能力表与小窝判断能力用的是同一个键、
 * 同一个请求（缓存共用，不多发）。另挂一个不自己发请求的旁观者（enabled: false）看同一条缓存，
 * 只为分清“还在读 / 读失败”——usePetCharacter 只交出数据，读失败时也是 null。
 */
function useLook(petId: string): Look {
  const { platform, pets } = useServices();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  const live = env.dataMode === "live";
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => platform.meta(), staleTime: 60_000, enabled: live });
  const state = usePetCharacter(petId);
  const watch = useQuery({ queryKey: queryKeys.characterFor(userId ?? "-", petId), queryFn: ({ signal }) => pets.character(petId, signal), enabled: false, retry: false });
  if (!live) return { phase: "demo" };
  if (meta.isPending) return { phase: "checking" };
  if (meta.isError) return { phase: "error", error: meta.error, retry: () => void meta.refetch() };
  const capabilities = meta.data.capabilities;
  const on = (key: string) => capabilities.some((capability) => capability.key === key && capability.status === "available");
  if (!on("character.state")) return { phase: "off" };
  const notDrawable = capabilities.some((capability) => capability.key === "character.regenerate" && capability.status === "not_configured");
  if (state) return { phase: "ready", state, idPhotoOn: on("character.id_photo"), notDrawable };
  if (watch.isError) return { phase: "error", error: watch.error, retry: () => void watch.refetch() };
  return { phase: "loading" };
}

function LookForPet({ pet }: { pet: CurrentPet }) {
  const look = useLook(pet.petId);
  const photoGuard = useLookPhotoGuard(pet.petId);
  // 来历、照片版本只有家庭里的宠物简介有（CurrentPet 只搬名字和照片）；live 下当前宠物就是这一只
  const brief = useOptionalCurrentHousehold()?.pet ?? null;
  const own = brief && brief.pet_id === pet.petId ? brief : null;
  const origin = own ? own.origin : null;
  // 补好、换好之后说的那一句：一页只留最新的一句（PhotoNote）
  const [photoNote, setPhotoNote] = useState<PhotoNote | null>(null);
  const notePage = (text: string) => setPhotoNote({ text, where: "page" });
  const addable = canAddPhoto(pet.photoUrl, origin);
  const replaceable = canReplacePhoto(pet.photoUrl, origin);
  const asset = look.phase === "ready" ? sceneAsset(look.state) : null;
  const line = look.phase === "ready" ? lookHeadline(look.state, { photoUrl: pet.photoUrl, origin }) : null;
  // 现在这张是没照片时生成的形象照：简介说的（photo_generated，新后端），或者形象一节此刻按只有生成照在说——换照片那张卡的标题就说“补一张 TA 的真实照片”
  const describedOnly = own?.photo_generated === true || (look.phase === "ready" && onlyGeneratedReference(look.state));
  return (
    <>
      <section className="ps-me-hero ps-look-hero" aria-label="TA 现在的样子">
        <PetPortrait petId={pet.petId} name={pet.name} species={pet.species} photoUrl={pet.photoUrl} size={68} />
        <div className="ps-me-hero__text">
          <h2>{pet.name}</h2>
          {line ? <p>{line}</p> : null}
        </div>
      </section>

      {addable ? (
        <AddPhoto petId={pet.petId} name={pet.name} onSettled={notePage} guard={photoGuard} />
      ) : photoNote?.where === "page" ? (
        <p className="ps-look-photo-note" role="status">
          {photoNote.text}
        </p>
      ) : null}
      {replaceable ? (
        <ReplacePhoto
          petId={pet.petId}
          name={pet.name}
          guard={photoGuard}
          photoUrl={pet.photoUrl}
          photoVersion={own?.photo_version}
          photoGenerated={own?.photo_generated}
          describedOnly={describedOnly}
          done={photoNote?.where === "replace" ? photoNote.text : null}
          onStart={() => setPhotoNote(null)}
          onDone={() => setPhotoNote({ text: REPLACE_PHOTO_DONE, where: "replace" })}
          onGone={notePage}
        />
      ) : null}

      {look.phase === "checking" || look.phase === "loading" ? <LoadingState lines={1} label="正在看看 TA 现在的样子…" /> : null}
      {look.phase === "error" ? <ErrorState error={look.error} onRetry={look.retry} /> : null}
      {look.phase === "demo" ? (
        <LookNote title="演示模式没有 TA 的星球形象">住进来以后，TA 在星球上的样子和证件照都照着 TA 的照片来画，可以在这里调整。</LookNote>
      ) : null}
      {look.phase === "off" ? <LookNote title="TA 的星球形象暂时看不了">过一阵再来看看。</LookNote> : null}
      {look.phase === "ready" ? (
        <>
          {asset ? <LookFigure state={look.state} asset={asset} name={pet.name} /> : null}
          <section className="ps-look-card" aria-label="调整形象与证件照">
            <LookIntro state={look.state} idPhotoOn={look.idPhotoOn} notDrawable={look.notDrawable} />
            {/* canAddPhoto 在这里的意思是“上面有放一张真实照片的入口”：补照片、换照片（只有生成的形象照时换成真实照片）都算；
                形象过时的那一句放到站姿图旁边说了（LookFigure），这里不再说第二遍——没有站姿图可放时才留在这里 */}
            <AdjustCharacter petId={pet.petId} state={look.state} origin={origin} canAddPhoto={addable || replaceable} showOutdatedNote={!asset} />
          </section>
          <BodyInfoCard petId={pet.petId} state={look.state} />
        </>
      ) : null}
      <WorldBodyCare petId={pet.petId} />
    </>
  );
}

/**
 * 补一张照片：选图 → 预览 → 确认才上传；上传中两个按钮都不能点（不连发）。换一张图就换一把幂等键；
 * 同一张图重试沿用原来那把（服务端按幂等键认出是同一次）。
 * 成功后刷新家庭（头像换成照片、这个入口换成“换一张照片”）、会话、这只的形象（“画不了”随之消失）、家园与公开页缓存。
 * 放上照片后后端自动排一次证件照（2026-09-26）：刷新回来的形象里证件照在排、在画，下面证件照一节先放这张照片、说“证件照正在画”，
 * 并复查到画好为止；这里不替主人发任何生成请求。
 */
type LookPhotoGuard = { selection: PhotoUnknownSelection; scope: string; ready: boolean; touch(): void };
/** Shared by add/replace, bound to confirmed actor/household/pet; photo version is deliberately not scope. */
function useLookPhotoGuard(petId: string): LookPhotoGuard {
  const current = useOptionalCurrentHousehold();
  const scope = JSON.stringify([current?.userId ?? null, current?.household?.household_id ?? null, petId]);
  const selection = useRef(new PhotoUnknownSelection()).current;
  selection.syncScope(scope);
  const alive = useRef(true);
  const [, redraw] = useState(0);
  useEffect(() => { alive.current = true; selection.activate(); return () => { alive.current = false; selection.dispose(); }; }, [selection]);
  return { selection, scope, ready: Boolean(current?.userId && current.household && current.pet?.pet_id === petId), touch: () => { if (alive.current) redraw((v) => v + 1); } };
}
function useLookPhotoSelection(guard: LookPhotoGuard, commit: (file: File) => void) {
  const alive = useRef(true), approved = useRef<string | null>(null);
  const [checking, setChecking] = useState(false), [note, setNote] = useState<string | null>(null);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { setChecking(false); setNote(null); }, [guard.scope]);
  const choose = (file: File) => {
    if (!guard.ready || guard.selection.isPending) return;
    if (!guard.selection.hasUnknown) {
      guard.selection.cancelSelection(); setChecking(false); setNote(null);
      approved.current = guard.scope; commit(file); return;
    }
    setChecking(true);
    void guard.selection.select(file).then((decision) => {
      if (!alive.current || decision === "stale" || decision === "pending") return;
      setChecking(false);
      if (decision === "different") { approved.current = guard.scope; setNote(null); commit(file); }
      else setNote(decision === "same" ? "这还是同一张照片，原来的结果还没确认。请选另一张。" : "这张照片暂时没核对好，原来的结果先保留，稍后再选。");
    });
  };
  return { alive, checking, note, choose, ready: guard.ready && approved.current === guard.scope, cancel: () => { guard.selection.cancelSelection(); setChecking(false); setNote(null); } };
}

function AddPhoto({ petId, name, onSettled, guard }: { petId: string; name: string; onSettled: (note: string) => void; guard: LookPhotoGuard }) {
  const { pets } = useServices();
  const refresh = usePhotoRefresh(petId);
  const [file, setFile] = useState<File | null>(null);
  const { preview, unreadable, setUnreadable } = usePhotoPreview(file);
  const keyRef = useRef(newIdempotencyKey("pet-photo"));
  // 这一张照片连着几次没认出来（换一张从 0 数起）
  const [unclearTimes, setUnclearTimes] = useState(0);
  const selectionView = useLookPhotoSelection(guard, (next) => {
    setFile(next); setUnreadable(false); setUnclearTimes(0);
    keyRef.current = newIdempotencyKey("pet-photo"); upload.reset();
  });
  const upload = useMutation({
    mutationFn: ({ photo, attempt }: { photo: File; attempt: PhotoSubmission }) => {
      if (!guard.selection.current(attempt)) throw new Error("PHOTO_SELECTION_STALE");
      return pets.addPhoto(petId, photo, attempt.key);
    },
    onSuccess: async (_, { attempt }) => {
      if (!guard.selection.settle(attempt, false)) return;
      guard.touch(); if (!selectionView.alive.current) return;
      onSettled(ADD_PHOTO_DONE);
      await refresh();
    },
    onError: async (error, { attempt }) => {
      const apiError = toApiError(error);
      const unresolved = photoResultUncertain(apiError);
      if (!guard.selection.settle(attempt, unresolved)) return;
      guard.touch(); if (!selectionView.alive.current || unresolved) return;
      // Only a complete native rejection or explicit before-send receipt permits a new key.
      if (photoRejectedForSure(apiError) || photoBeforeSend(apiError)) keyRef.current = newIdempotencyKey("pet-photo");
      if (isPhotoUnclear(apiError)) setUnclearTimes((times) => times + 1);
      // 已经有照片 / 其实是领养来的：这个入口本不该出现。说一句，再刷新，页面换成 TA 真实的样子（入口随之消失，这句留着）
      const reason = toApiError(error).details?.reason;
      if (reason === "photo_exists" || reason === "not_own_pet") {
        onSettled(addPhotoFailureText(error));
        await refresh();
      }
    },
  });
  const unknown = upload.isError && photoResultUncertain(toApiError(upload.error));
  // 没配好：再发也一样，发送键灰掉（UI-PHOTO-RECOVERY-RETRYABLE-01；发送只有这颗键一个入口）
  const notReady = upload.isError && photoNotReady(toApiError(upload.error));
  const sendPhoto = (photo: File) => {
    if (unknown || !selectionView.ready || selectionView.checking || guard.selection.busy || unreadable) return;
    const attempt = guard.selection.begin(photo, keyRef.current);
    if (attempt) { guard.touch(); upload.mutate({ photo, attempt }); }
  };
  return (
    <section className="ps-look-card ps-look-photo" aria-labelledby="look-photo-title" data-testid="add-photo">
      <h3 id="look-photo-title">补一张 TA 的照片</h3>
      <p className="ps-look-photo__lead">入住时没放照片也没关系，现在可以补一张。</p>
      {preview && !unreadable ? <img className="ps-look-photo__preview" src={preview} alt={`准备放上的${name}的照片`} onError={() => setUnreadable(true)} /> : null}
      <div className="ps-look-photo__actions">
        <label className={`ps-btn ps-btn--secondary ps-look-photo__pick${upload.isPending ? " is-disabled" : ""}`}>
          {file ? "换一张" : "选一张照片"}
          <input
            type="file"
            accept="image/*"
            className="visually-hidden"
            disabled={upload.isPending}
            onChange={(event) => {
              const next = event.target.files?.[0] ?? null;
              event.target.value = "";
              if (!next) return;
              selectionView.choose(next);
            }}
          />
        </label>
        {file && !unreadable ? (
          <Button variant="primary" loading={upload.isPending} disabled={upload.isPending || unknown || notReady || !selectionView.ready || selectionView.checking || guard.selection.busy} onClick={() => sendPhoto(file)}>
            {unknown ? "结果还没确认" : upload.isError && isPhotoUnclear(toApiError(upload.error)) ? "重新识别" : upload.isError && photoBeforeSend(toApiError(upload.error)) ? "再试一次" : "放上这张照片"}
          </Button>
        ) : null}
      </div>
      <p className="ps-look-photo__privacy">{ADD_PHOTO_PRIVACY}</p>
      <PhotoDataUseNote />
      {unreadable ? (
        <p className="ps-form-error" role="alert">
          {ADD_PHOTO_UNREADABLE}
        </p>
      ) : selectionView.note ? <p className="ps-form-error" role="alert">{selectionView.note}</p> : upload.isError ? (
        <PhotoFailure text={addPhotoFailureText(upload.error, unclearTimes)} />
      ) : null}
    </section>
  );
}

/**
 * 换一张照片（2026-09-25 用户拍板：已有照片也能换，含没照片时生成的形象照）。和补照片同一个 PUT，主人确认后带 replace_version。
 * - 家庭简介里带着现在那张的版本（photo_version，2026-09-25 起）：选好图，预览和问题一起出来——“要换掉现在这张吗？”
 *   （photo_generated 时按钮写“换掉 AI 生成的形象照”）→ 主人点了，带着这个版本**只传一次**。问的“现在这张”和带上的版本
 *   来自同一份简介：页面上给主人看的是哪一张，换掉的就是哪一张。
 * - 简介里没有版本（旧后端还没有这两个字段，或者给的是 null）：退回原来的做法——点“换上这张照片”先照常发一次（addPhoto，
 *   不带版本），服务端说已经有照片（409 photo_exists，details 带 version 与 generated）再问，主人点了才带上那个 version 换。
 * - 确认之后照片又被家里别人换过（409 photo_changed）：刷新，“现在这张”换成家里人放的那张，说一句，等主人再点——**不自动重发**；
 * - 幂等键跟着请求体走：换一张图全部换新；同一张图、同一个版本（网络失败后重试）沿用原来那把；版本一变换一把新的
 *   （同一把键配不同请求体，服务端回 409）；
 * - 问的时候顺带说一句“换好后，证件照会照新照片重画。”（2026-09-26 起后端自动重画证件照；形象不跟着画，要调整由主人在下面点）；
 * - 成功后刷新（头像换成新图：photo_url 带新的 ?v=，只用服务端给的地址；形象查询也重取），说一句“照片换好了。”；
 *   这里不替主人发任何生成请求。
 * - 说的那一句归页面管（PhotoNote，一页只留最新的一句）：选好一张图就是下一次操作开始，onStart 收掉上一句（这张卡的“换好了”、
 *   卡外的“放好了”都算）；换好了 onDone，由页面放回这张卡里（done）。
 */
function ReplacePhoto({ petId, name, photoUrl, photoVersion, photoGenerated, describedOnly = false, done, onStart, onDone, onGone, guard }: {
  guard: LookPhotoGuard;
  petId: string;
  name: string;
  photoUrl: string | null;
  /** 家庭简介里现在那张的版本：是字符串才算知道（undefined＝旧后端没有这个字段；null＝简介说没有照片） */
  photoVersion?: string | null;
  /** 家庭简介里现在那张是不是没照片时生成的形象照 */
  photoGenerated?: boolean;
  /**
   * 页面上说的是“只有按描述画的样子”（简介的 photo_generated，或形象一节按只有生成照在说）：标题跟下面那句一样说“补一张 TA 的真实照片”；
   * 顶部、下面都已说过是按描述画的，这张卡就不再加说明（不说第三遍）。
   */
  describedOnly?: boolean;
  /** 页面此刻要在这张卡里说的那一句（换好了）；没有为 null */
  done: string | null;
  /** 选好了一张图（下一次操作开始）：页面收掉上一句 */
  onStart: () => void;
  /** 换好了：页面把“照片换好了。”放进这张卡 */
  onDone: () => void;
  onGone: (note: string) => void;
}) {
  const { pets } = useServices();
  const refresh = usePhotoRefresh(petId);
  const [file, setFile] = useState<File | null>(null);
  const { preview, unreadable, setUnreadable } = usePhotoPreview(file);
  // 旧后端时由服务端 409 报来的“现在那张”：版本（换的时候原样带回）与是不是生成的形象照
  const [ask, setAsk] = useState<{ version: string; generated: boolean } | null>(null);
  const keys = useRef(new Map<string, string>());
  // 这一张照片连着几次没认出来（换一张从 0 数起）
  const [unclearTimes, setUnclearTimes] = useState(0);
  const askRef = useRef<HTMLParagraphElement>(null);
  const known = typeof photoVersion === "string" ? { version: photoVersion, generated: photoGenerated === true } : null;
  // 在问的那一版：简介知道版本时，选好图（预览打得开）就问；不知道时，等服务端报来再问
  const question = file && !unreadable ? (known ?? ask) : null;
  const asking = question !== null;
  useEffect(() => {
    // 问题出现时把焦点移过去：原来那颗“换上这张照片”已经不在了，读屏要能听到在问什么
    if (asking) askRef.current?.focus();
  }, [asking]);
  const keyFor = (version: string | null) => {
    const slot = version ?? "";
    let key = keys.current.get(slot);
    if (!key) {
      key = newIdempotencyKey("pet-photo");
      keys.current.set(slot, key);
    }
    return key;
  };
  const selectionView = useLookPhotoSelection(guard, (next) => {
    setFile(next); setUnreadable(false); onStart(); setUnclearTimes(0);
    setAsk(null); keys.current = new Map(); send.reset();
  });
  const send = useMutation({
    mutationFn: ({ photo, version, attempt }: { photo: File; version: string | null; attempt: PhotoSubmission }) => {
      if (!guard.selection.current(attempt)) throw new Error("PHOTO_SELECTION_STALE");
      return version === null ? pets.addPhoto(petId, photo, attempt.key) : pets.replacePhoto(petId, photo, version, attempt.key);
    },
    onSuccess: async (_, { attempt }) => {
      if (!guard.selection.settle(attempt, false)) return;
      guard.touch(); if (!selectionView.alive.current) return;
      setFile(null);
      setAsk(null);
      onDone();
      await refresh();
    },
    onError: async (error, { attempt }) => {
      const apiError = toApiError(error);
      const unresolved = photoResultUncertain(apiError);
      if (!guard.selection.settle(attempt, unresolved)) return;
      guard.touch(); if (!selectionView.alive.current || unresolved) return;
      if (photoRejectedForSure(apiError) || photoBeforeSend(apiError)) keys.current = new Map();
      if (isPhotoUnclear(apiError)) setUnclearTimes((times) => times + 1);
      const details = apiError.details;
      const reason = details?.reason;
      const version = details?.version;
      if (reason === "photo_exists" || reason === "photo_changed") {
        // 旧后端：按服务端报来的版本问主人（或换成新版本再问一次）；简介带版本时以刷新回来的简介为准（question 里 known 优先）。
        // 两种都刷新，让“现在这张”就是此刻的那张
        setAsk(typeof version === "string" ? { version, generated: details?.generated === true } : null);
        await refresh();
      } else if (reason === "not_own_pet") {
        onGone(addPhotoFailureText(error));
        await refresh();
      }
    },
  });
  // 成功、先不换之后图都清掉了：再发只能先选一张新图，键在选图时统一换新（onChange），这里不必再换
  const unknown = send.isError && photoResultUncertain(toApiError(send.error));
  // 没配好：再发也一样，两处发送键都灰掉（UI-PHOTO-RECOVERY-RETRYABLE-01；发送只有这两颗键两个入口）
  const notReady = send.isError && photoNotReady(toApiError(send.error));
  const sendPhoto = (photo: File, version: string | null) => {
    if (unknown || !selectionView.ready || selectionView.checking || guard.selection.busy || unreadable) return;
    const attempt = guard.selection.begin(photo, keyFor(version));
    if (attempt) { guard.touch(); send.mutate({ photo, version, attempt }); }
  };
  const cancel = () => {
    if (unknown || guard.selection.isPending) return;
    selectionView.cancel(); setFile(null); setAsk(null); send.reset();
  };
  const failure = send.isError ? replacePhotoFailureText(send.error, unclearTimes) : null;
  // 这会儿没认出来（确定终态）：再发的按钮写明“重新识别”，点了换新键（c84a 1046Z D1046-2）
  const recheck = send.isError && isPhotoUnclear(toApiError(send.error));
  const beforeSend = send.isError && photoBeforeSend(toApiError(send.error));
  const actionLabel = unknown ? "结果还没确认" : recheck ? "重新识别" : beforeSend ? "再试一次" : null;
  // 家里人刚换过（photo_changed）不是出错：用提示色、读屏用 status 播报；放不了照片用中性说明色（PhotoFailure）；其余（网络断了、图用不了……）才是红字 alert（2026-09-25 巡检 P2）
  const changed = send.isError && toApiError(send.error).details?.reason === "photo_changed";
  const message = failure ? (
    changed ? (
      <p className="ps-look-photo__notice" role="status">
        {failure}
      </p>
    ) : (
      <PhotoFailure text={failure} />
    )
  ) : null;
  return (
    <section className="ps-look-card ps-look-photo" aria-labelledby="look-replace-title" data-testid="replace-photo">
      <h3 id="look-replace-title">{describedOnly ? REPLACE_PHOTO_TITLE_REAL : REPLACE_PHOTO_TITLE}</h3>
      {question && file ? (
        <div className="ps-look-swap">
          <p className="ps-look-swap__ask" ref={askRef} tabIndex={-1}>
            {REPLACE_PHOTO_ASK}
          </p>
          {/* 问的时候出的状况（家里人刚换过、网络断了、图用不了）紧跟在问题下面说，先看到再决定 */}
          {message}
          <div className="ps-look-swap__pair">
            <figure>
              <img src={photoUrl ?? undefined} alt={`${name}现在的照片`} />
              <figcaption>现在</figcaption>
            </figure>
            <figure>
              {/* 简介知道版本时选好图就到这一步，预览只在这里：打不开就说用不了、退回去让主人换一张（2026-09-25 巡检 P2 同一条） */}
              <img src={preview ?? undefined} alt={`准备换上的${name}的照片`} onError={() => setUnreadable(true)} />
              <figcaption>换成</figcaption>
            </figure>
          </div>
          {/* 点之前说清换了以后会怎样：证件照会自动照新照片重画（形象不跟着画） */}
          <p className="ps-look-photo__lead ps-look-swap__note">{REPLACE_PHOTO_ID_NOTE}</p>
          <div className="ps-look-photo__actions">
            <Button variant="secondary" disabled={send.isPending || unknown} onClick={cancel}>
              先不换
            </Button>
            <Button variant="primary" loading={send.isPending} disabled={unknown || notReady || !selectionView.ready || selectionView.checking || guard.selection.busy} onClick={() => sendPhoto(file, question.version)}>
              {actionLabel ?? "换掉现在这张"}
            </Button>
            {unknown ? <label className="ps-btn ps-btn--secondary ps-look-photo__pick">换一张
              <input type="file" accept="image/*" className="visually-hidden" disabled={send.isPending} onChange={(event) => { const next = event.target.files?.[0]; event.target.value = ""; if (next) selectionView.choose(next); }} />
            </label> : null}
          </div>
        </div>
      ) : (
        <>
          {describedOnly ? null : <p className="ps-look-photo__lead">想用另一张照片，可以在这里换。</p>}
          {preview && !unreadable ? <img className="ps-look-photo__preview" src={preview} alt={`准备换上的${name}的照片`} onError={() => setUnreadable(true)} /> : null}
          <div className="ps-look-photo__actions">
            <label className={`ps-btn ps-btn--secondary ps-look-photo__pick${send.isPending ? " is-disabled" : ""}`}>
              {file ? "换一张" : "选一张照片"}
              <input
                type="file"
                accept="image/*"
                className="visually-hidden"
                disabled={send.isPending}
                onChange={(event) => {
                  const next = event.target.files?.[0] ?? null;
                  event.target.value = "";
                  if (!next) return;
                  selectionView.choose(next);
                }}
              />
            </label>
            {file && !unreadable ? (
              <Button variant="primary" loading={send.isPending} disabled={unknown || notReady || !selectionView.ready || selectionView.checking || guard.selection.busy} onClick={() => sendPhoto(file, null)}>
                {actionLabel ?? "换上这张照片"}
              </Button>
            ) : null}
          </div>
        </>
      )}
      <p className="ps-look-photo__privacy">{ADD_PHOTO_PRIVACY}</p>
      <PhotoDataUseNote />
      {done ? (
        <p className="ps-look-photo__done" role="status">
          {done}
        </p>
      ) : null}
      {unreadable ? (
        <p className="ps-form-error" role="alert">
          {ADD_PHOTO_UNREADABLE}
        </p>
      ) : selectionView.note ? <p className="ps-form-error" role="alert">{selectionView.note}</p> : question && file ? null : (
        message
      )}
    </section>
  );
}

/**
 * 补照片、换照片没成的那一句。「现在还放不了照片」（照片功能这会儿没配好，before_send 且 retryable=false）不是主人的错（I 10-14）：
 * 不用错误红，用中性说明色（和同一节隐私那句同色），读屏用 status 播报；其余照旧红字 alert。
 */
function PhotoFailure({ text }: { text: string }) {
  return text === ADD_PHOTO_NOT_READY || text === REPLACE_PHOTO_NOT_READY ? (
    <p className="ps-look-photo__note" role="status">
      {text}
    </p>
  ) : (
    <p className="ps-form-error" role="alert">
      {text}
    </p>
  );
}

/** 选好的图先在本机预览（不上传）；预览打不开时由调用方标成“用不了”（2026-09-25 巡检 P2），换一张图由调用方清掉。 */
function usePhotoPreview(file: File | null) {
  const [preview, setPreview] = useState<string | null>(null);
  const [unreadable, setUnreadable] = useState(false);
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return { preview, unreadable, setUnreadable };
}

/** 照片变了以后要刷新的缓存：家庭（头像、入口）、会话、这只的形象（画不画得了、证件照）、家园与公开页（里面都有照片地址）。 */
function usePhotoRefresh(petId: string) {
  const queryClient = useQueryClient();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["households"] }),
      queryClient.invalidateQueries({ queryKey: queryKeys.session }),
      queryClient.invalidateQueries({ queryKey: queryKeys.characterFor(userId ?? "-", petId) }),
      queryClient.invalidateQueries({ queryKey: ["world", "home"] }),
      queryClient.invalidateQueries({ queryKey: ["public"] }),
    ]);
}

/** 那一句说明；按钮名“画证件照”连着引号不拆行（窄屏上不会断成“画证 / 件照”）。页面上没有那个按钮时，一个字都不提它。 */
function LookIntro({ state, idPhotoOn, notDrawable }: { state: CharacterState; idPhotoOn: boolean; notDrawable: boolean }) {
  if (state.blocked_reason === "no_reference_photo") return <p className="ps-look-intro">{LOOK_INTRO_NO_PHOTO}</p>;
  // 只有生成的形象照（generated_reference_only）：服务端不照它画、页面上也没有调整按钮，不说“照着照片画、可以在这里调整”；
  // 这张形象照是怎么回事、要不要补照片，由下面形象一节按来历说一次（generatedReferenceText），这里不再说第二遍（2026-09-25 巡检 P1）
  if (isGeneratedReferenceOnly(state.blocked_reason)) return null;
  // 这个环境画不出形象（W5）：不说“可以在这里调整”
  if (notDrawable) return <p className="ps-look-intro">{LOOK_INTRO_NOT_DRAWABLE}</p>;
  if (!offersIdPhotoButton(state, idPhotoOn)) return <p className="ps-look-intro">{LOOK_INTRO}</p>;
  const quoted = "“画证件照”";
  const [before, after] = LOOK_INTRO_WITH_ID_PHOTO.split(quoted);
  return (
    <p className="ps-look-intro">
      {before}
      <span className="ps-look-keep">{quoted}</span>
      {after}
    </p>
  );
}

/** 生效形象的站姿图：与小窝同一套落位（中性站姿的身体高度占满舞台，落地点对准底边中点）。 */
function LookFigure({ state, asset, name }: { state: CharacterState; asset: CharacterAsset; name: string }) {
  const round = (value: number) => Math.round(value * 100) / 100;
  const heightPct = round((asset.height / referenceBodyHeight(state, asset)) * 100);
  return (
    <figure className="ps-look-stage">
      <span className="ps-look-stage__body">
        <span className="ps-look-stage__shadow" aria-hidden="true" />
        <img
          src={asset.url}
          alt={`${name}的星球形象`}
          style={{ height: `${heightPct}%`, aspectRatio: `${asset.width} / ${asset.height}`, transform: `translate(${round(-asset.anchor.x * 100)}%, ${round((1 - asset.anchor.y) * 100)}%)` }}
        />
      </span>
      <figcaption>
        星球上的 TA
        {/* 换过照片、这一套还是照着之前那张画的：就在图旁边说（巡检 R6-18：原来在下面调整栏里，离图远）。
            前面留一个空格：另起一行显示时看不见，读屏连着读时两句之间有个停顿 */}
        {state.active?.outdated ? <span className="ps-look-stage__outdated">{` ${FIGURE_OUTDATED_NOTE}`}</span> : null}
      </figcaption>
    </figure>
  );
}

function LookNote({ title, children }: { title: string; children: string }) {
  return (
    <div className="ps-look-card ps-look-note" role="note">
      <span className="ps-look-note__icon" aria-hidden="true">
        <Icon name="sparkle" size={22} />
      </span>
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}
